import React, { useEffect, useMemo, useState } from "react";
import { CgAddR } from "react-icons/cg";
import { TailSpin } from "react-loader-spinner";
import { AiOutlineCloseCircle } from "react-icons/ai";

import type {
  GiftCardCatalog,
  GiftCardListStatus,
} from "../../../lib/api/giftCardCatalog";
import { useGiftCardsListQuery } from "../../../lib/api/useGiftCardCatalogQueries";

function categoryToStatus(category: number): GiftCardListStatus {
  if (category === 2) return "active";
  if (category === 3) return "inactive";
  return "all";
}

type CardModalProps = {
  cardId: string;
  onSelectCard: (id: string) => void;
  setUploadCardImage: React.Dispatch<React.SetStateAction<boolean>>;
  setCardModal: React.Dispatch<React.SetStateAction<boolean>>;
};

const CardModal: React.FC<CardModalProps> = ({
  cardId,
  onSelectCard,
  setUploadCardImage,
  setCardModal,
}) => {
  const [category, setCategory] = useState(1);
  const listStatus = categoryToStatus(category);

  const {
    data: giftCards = [],
    isPending,
    isError,
    error,
    refetch,
  } = useGiftCardsListQuery(listStatus, { enabled: true });

  const sortedGiftCards = useMemo(() => {
    return [...giftCards].sort((a, b) => {
      if (a.isActive && !b.isActive) return -1;
      if (!a.isActive && b.isActive) return 1;
      return 0;
    });
  }, [giftCards]);

  useEffect(() => {
    if (isPending) return;
    if (giftCards.length === 0) return;
    const found = Boolean(cardId && giftCards.some((c) => c._id === cardId));
    if (!found) {
      onSelectCard(giftCards[0]._id);
    }
  }, [giftCards, cardId, onSelectCard, listStatus, isPending]);

  const listForCategory = (): GiftCardCatalog[] => {
    if (category === 1) return sortedGiftCards;
    return giftCards;
  };

  const displayList = listForCategory();
  const axiosMsg =
    error && typeof error === "object" && "response" in error
      ? String(
          (error as { response?: { data?: { message?: string } } }).response
            ?.data?.message ?? "Could not load cards."
        )
      : "Could not load cards.";

  return (
    <div className="fixed inset-0 z-[70] flex font-manrope justify-center items-start overflow-auto pt-16 pb-16  bg-black bg-opacity-30  backdrop-blur-sm">
      <div className="w-11/12 lgss:w-2/3 transition-all p-8 ease-in duration-1000 rounded-xl bg-[#0D0D0D] min-h-[400px] max-h-[min(520px,85vh)] flex flex-col">
        <div className="flex justify-between items-center shrink-0">
          <div className="flex justify-start mt-4 items-start gap-6">
            <button type="button" className="bg-transparent border-0 p-0" onClick={() => setCategory(1)}>
              <h4
                className={
                  category === 1
                    ? "font-semibold cursor-pointer text-[13px] text-secondary_button"
                    : " cursor-pointer text-[13px] text-[#C6D0D7]"
                }
              >
                All cards
              </h4>
              {category === 1 ? (
                <div className="w-full h-[4px] bg-secondary_button rounded-full mt-3" />
              ) : null}
            </button>
            <button type="button" className="bg-transparent border-0 p-0" onClick={() => setCategory(2)}>
              <h4
                className={
                  category === 2
                    ? "font-semibold cursor-pointer text-[13px] text-secondary_button"
                    : " cursor-pointer text-[13px] text-[#C6D0D7]"
                }
              >
                Active cards
              </h4>
              {category === 2 ? (
                <div className="w-full h-[4px] bg-secondary_button rounded-full mt-3" />
              ) : null}
            </button>
            <button type="button" className="bg-transparent border-0 p-0" onClick={() => setCategory(3)}>
              <h4
                className={
                  category === 3
                    ? "font-semibold cursor-pointer text-[13px] text-secondary_button"
                    : " cursor-pointer text-[13px] text-[#C6D0D7]"
                }
              >
                Deactivated cards
              </h4>
              {category === 3 ? (
                <div className="w-full h-[4px] bg-secondary_button rounded-full mt-3" />
              ) : null}
            </button>
          </div>
          <AiOutlineCloseCircle
            onClick={() => setCardModal(false)}
            className="text-[24px] text-secondary_button cursor-pointer"
          />
        </div>
        <div className="w-full rounded-full h-[1px] bg-[#3A4852] shrink-0" />
        <div className="w-full mt-6 flex-1 min-h-0 overflow-y-auto">
          {isError ? (
            <div className="rounded-lg border border-red-500/35 bg-red-500/10 px-4 py-3 text-[13px] text-red-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <span>{axiosMsg}</span>
              <button
                type="button"
                onClick={() => void refetch()}
                className="shrink-0 rounded-lg bg-red-500/20 px-3 py-1.5 text-[12px] font-semibold text-red-200 border border-red-500/40"
              >
                Retry
              </button>
            </div>
          ) : isPending ? (
            <div className="flex justify-center py-8">
              <TailSpin color="#0B84CB" height={40} width={40} />
            </div>
          ) : category === 1 ? (
            displayList.length >= 1 ? (
              <div className="w-full mt-2 flex flex-wrap items-center overflow-auto justify-start gap-4 scrollbar-thumb-[#60B7F6]  scrollbar-track-white  scrollbar-thin">
                {displayList.map((allCard) => (
                  <div key={allCard._id} className="h-[58px] w-[87px]">
                    <img
                      src={allCard.thumbnail}
                      alt=""
                      onClick={() => {
                        onSelectCard(allCard._id);
                        setCardModal(false);
                      }}
                      className={
                        allCard.isActive
                          ? "h-full w-full object-cover  cursor-pointer"
                          : "h-full w-full object-cover filter grayscale cursor-pointer"
                      }
                    />
                  </div>
                ))}
                <CgAddR
                  onClick={() => {
                    setCardModal(false);
                    setUploadCardImage((prev) => !prev);
                  }}
                  className=" cursor-pointer text-secondary_button text-[40px]"
                />
              </div>
            ) : (
              <CgAddR
                onClick={() => {
                  setCardModal(false);
                  setUploadCardImage((prev) => !prev);
                }}
                className=" cursor-pointer text-secondary_button text-[40px]"
              />
            )
          ) : displayList.length >= 1 ? (
            <div className="w-full mt-2 flex items-center justify-start gap-4 scrollbar-thumb-[#60B7F6] overflow-auto scrollbar-track-white  scrollbar-thin">
              {displayList.map((c) => (
                <div key={c._id} className="h-[58px] w-[87px]">
                  <img
                    src={c.thumbnail}
                    alt=""
                    onClick={() => {
                      onSelectCard(c._id);
                      setCardModal(false);
                    }}
                    className={
                      category === 3
                        ? "h-full w-full object-cover grayscale filter cursor-pointer"
                        : "h-full w-full object-cover cursor-pointer"
                    }
                  />
                </div>
              ))}
            </div>
          ) : (
            <h4 className="font-bold text-[12px] font-sora text-white">
              {category === 2 ? "No activated card" : "No deactivated card"}
            </h4>
          )}
        </div>
      </div>
    </div>
  );
};

export default CardModal;
